<template>
  <div
    class="reason-badge-component-wrapper is-clickable"
    v-if="props.reason"
    @contextmenu="openContextMenu($event, `${type}_reasons`, filterValue)"
    @dblclick="doubleClick($event, `${type}_reasons`, filterValue)"
  >
    <div
      class="reason-badge-element-wrapper"
      data-qa="reason-badge"
      :class="{ 'is-expandable': isExpandable }"
      @click="badgeClicked()"
    >
      <div class="expander is-clickable" v-if="isExpandable">
        <icon-remove
          v-if="isExpanded"
          v-b-tooltip="t('eventsLog.panel.mainCard.reasons.collapse')"
        />
        <icon-add v-else v-b-tooltip="t('eventsLog.panel.mainCard.reasons.expand')" />
      </div>
      <div class="reason-badge rbz-text-medium" :class="type">
        <span>
          {{ reason.type }}
        </span>
        :
        <span>
          {{ reason.name }}
        </span>
      </div>
    </div>
    <div class="additional-information" v-if="isExpanded && reason.extra">
      <template v-if="reason.type === 'Content Filter Rule'">
        <div class="reason-type-wrapper">
          <div class="location">
            {{ t(`eventsLog.panel.mainCard.reasons.${reason.extra.location}`) }}:
          </div>
          <div class="name ellipsis" ref="reasonBadgeNameRef" v-b-tooltip="reasonBadgeNameTooltip">
            {{ reason.extra.name }}
          </div>
          <div
            class="value ellipsis"
            ref="reasonBadgeValueRef"
            v-b-tooltip="reasonBadgeValueTooltip"
          >
            {{ reason.extra.value }}
          </div>
        </div>
        <div
          class="reason-type-wrapper"
          v-if="reason.extra?.fingerprint && reason.name?.includes('libinjection')"
        >
          <div class="fingerprint">
            {{ t('eventsLog.panel.mainCard.fingerprint') }}
          </div>
          <div
            class="fingerprint-value ellipsis"
            ref="reasonBadgeFingerprintRef"
            v-b-tooltip="reasonBadgeFingerprintTooltip"
          >
            {{ reason.extra.fingerprint }}
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { EventLogReason } from '@/types';
import { useI18n } from 'vue-i18n';
import { computed, ref } from 'vue';
import IconAdd from 'ui-component-library/assets/icons/IconAdd.vue';
import IconRemove from 'ui-component-library/assets/icons/IconRemove.vue';
import Utils from 'ui-component-library/helpers/Utils';

const { t } = useI18n();

interface EventsLogPanelReasonBadgeProps {
  reason: EventLogReason;
  type: 'block' | 'monitor';
  urlPath?: string;
}

const props = defineProps<EventsLogPanelReasonBadgeProps>();

interface EventsLogPanelReasonBadgeEmits {
  (
    e: 'context-menu',
    { event, property, value }: { event: Event; property: string; value: string }
  ): void;

  (
    e: 'double-click',
    { event, property, value }: { event: Event; property: string; value: string }
  ): void;
}

const emit = defineEmits<EventsLogPanelReasonBadgeEmits>();

let clickTimer: number = null;
let clickCounter = 0;
const badgeClicked = () => {
  clickCounter++;
  if (clickCounter === 1) {
    clickTimer = window.setTimeout(() => {
      clickCounter = 0;
      if (isExpandable.value) {
        isExpanded.value = !isExpanded.value;
      }
    }, 200);
  }
  if (clickCounter === 2) {
    clearTimeout(clickTimer);
    clickCounter = 0;
  }
};

const openContextMenu = (event: Event, property: string, value: string) => {
  emit('context-menu', {
    event,
    property,
    value,
  });
};

const doubleClick = (event: Event, property: string, value: string) => {
  emit('double-click', {
    event,
    property,
    value,
  });
};

const filterValue = computed(() => {
  let value = `${props.reason.type}:${props.reason.name}`;
  if (props.reason.extra.location) {
    value += `,${props.reason.extra.location}`;
  }
  const hasExtraName = props.reason.extra.name != null;
  const hasExtraValue = props.reason.extra.value != null;
  if (hasExtraName && hasExtraValue) {
    value += `:${props.reason.extra.name}:${props.reason.extra.value}`;
  } else if (props.reason.extra.location === 'path' && props.urlPath) {
    value += `:${props.urlPath}`;
  }
  return value;
});

const isExpanded = ref(false);

const typesWithExpander = ['Content Filter Rule'];
const isExpandable = computed(() => {
  return typesWithExpander.includes(props.reason.type);
});

const reasonBadgeNameRef = ref(null);
const reasonBadgeNameTooltip = computed(() => {
  return Utils.elementTreeOverflow(reasonBadgeNameRef.value) ? props.reason.extra.name : '';
});
const reasonBadgeValueRef = ref(null);
const reasonBadgeValueTooltip = computed(() => {
  return Utils.elementTreeOverflow(reasonBadgeValueRef.value) ? props.reason.extra.value : '';
});
const reasonBadgeFingerprintRef = ref(null);
const reasonBadgeFingerprintTooltip = computed(() => {
  return Utils.elementTreeOverflow(reasonBadgeFingerprintRef.value)
    ? props.reason.extra.fingerprint
    : '';
});
</script>

<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

.reason-badge-element-wrapper {
  align-items: center;
  display: flex;
  gap: 4px;

  &.is-expandable:hover {
    .block {
      background: map.get(colors.$colors, 'shilo');
    }
  }

  &.is-expandable:active {
    .block {
      background: map.get(colors.$colors, 'pink-flare');
    }
  }
}

.reason-badge {
  border-radius: 4px;
  box-sizing: border-box;
  padding: 4px 8px;
}

.block {
  background: map.get(colors.$colors, 'your-pink');
  color: map.get(colors.$colors, 'crimson');
}

.monitor {
  background: map.get(colors.$colors, 'barley-white');
  color: map.get(colors.$colors, 'anzac');
}

.expander {
  display: flex;
}

.additional-information {
  display: flex;
  gap: 8px;
  margin-top: 4px;
  flex-direction: column;

  .reason-type-wrapper {
    display: flex;
    gap: 8px;

    .fingerprint {
      color: map.get(colors.$colors, 'bright-gray');
      text-decoration-line: underline;
      width: fit-content;
    }

    .fingerprint-value {
      color: map.get(colors.$colors, 'shuttle-gray');
      max-width: 50%;
      min-width: 0;
    }
  }

  .location {
    color: map.get(colors.$colors, 'bright-gray');
    text-decoration-line: underline;
    width: fit-content;
  }

  .name {
    color: map.get(colors.$colors, 'bright-gray');
    max-width: 30%;
    min-width: 0;
  }

  .value {
    color: map.get(colors.$colors, 'shuttle-gray');
    max-width: 50%;
    min-width: 0;
  }
}
</style>
